/**
 * The one card-tokenization seam (TMMRSL-0015). Fetches bearer client-config, loads Stripe.js,
 * mounts a Card Element, and tokenizes. Never logs or stores the token.
 *
 * `scope` picks the config: `tenant` / `platform` read the open tenant's
 * `…/tenants/{slug}/billing/client-config?scope=` (no open tenant → `unavailable`); `reseller`
 * (TMMRSL-0046) reads the reseller-wide config for the reseller's own card and needs no tenant.
 */
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  billingClientConfigApi,
  type BillingClientConfigResult,
  type BillingClientConfigScope,
} from '@/api/billingClientConfig';
import { useThemeMode } from '@/context/theme-mode-context';
import { useLocale } from '@/i18n/useLocale';
import { TenantContext } from '@/tenant/tenantContext';
import { resolveTokens } from '@/theme';
import { stripeElementsLocale } from './stripeLocale';
import { clearStripeMemo, getStripe, type StripeClient } from './stripeSdk';

export type CardTokenStatus = 'loading' | 'notConfigured' | 'unsupported' | 'unavailable' | 'ready';

/** A tenant-bound scope, or `reseller` for the reseller's own card. */
export type CardConfigScope = BillingClientConfigScope | 'reseller';

export type TokenizeResult = { ok: true; token: string } | { ok: false; message: string | null };

const CARD_SELECTOR = '#card-container';

interface StripeCard {
  mount: (domElement: string | HTMLElement) => void;
  unmount: () => void;
  destroy: () => void;
  on: (
    event: 'change',
    handler: (event: { error?: { message?: string }; complete?: boolean }) => void,
  ) => void;
}

export function useCardToken(scope: CardConfigScope) {
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const { resolvedMode } = useThemeMode();
  // The card's own labels and messages follow the UI language (TMMRSL-0073).
  const elementsLocale = stripeElementsLocale(useLocale());
  const tenant = useContext(TenantContext);
  const slug = tenant?.slug;
  const [status, setStatus] = useState<CardTokenStatus>('loading');
  const [complete, setComplete] = useState(false);
  const [cardMessage, setCardMessage] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const stripeRef = useRef<StripeClient | null>(null);
  const cardRef = useRef<StripeCard | null>(null);

  useEffect(() => {
    let cancelled = false;
    let card: StripeCard | null = null;

    async function setup() {
      setStatus('loading');
      setComplete(false);
      setCardMessage(null);
      let result: BillingClientConfigResult;
      if (scope === 'reseller') {
        result = await billingClientConfigApi.getReseller();
      } else if (slug === undefined) {
        setStatus('unavailable');
        return;
      } else {
        result = await billingClientConfigApi.get(slug, scope);
      }
      if (cancelled) return;
      if (result.kind === 'unsupported') {
        setStatus('unsupported');
        return;
      }
      if (result.kind === 'unavailable') {
        setStatus('unavailable');
        return;
      }
      const stripe = await getStripe(result.config.publishableKey, result.config.stripeAccount);
      if (cancelled) return;
      if (!stripe) {
        setStatus('notConfigured');
        return;
      }
      const fontFamily = theme.typography.fontFamily;
      const appearance = {
        theme: resolvedMode === 'dark' ? ('night' as const) : ('stripe' as const),
        variables: {
          colorPrimary: tokens.brand,
          colorText: tokens.text,
          colorBackground: tokens.surface,
          ...(typeof fontFamily === 'string' ? { fontFamily } : {}),
        },
      };
      const elements = stripe.elements({ appearance, locale: elementsLocale });
      card = elements.create('card', { hidePostalCode: false }) as unknown as StripeCard;
      card.on('change', (event) => {
        setCardMessage(event.error?.message ?? null);
        setComplete(event.complete === true);
      });
      card.mount(CARD_SELECTOR);
      if (cancelled) {
        card.unmount();
        card.destroy();
        return;
      }
      stripeRef.current = stripe;
      cardRef.current = card;
      setStatus('ready');
    }

    void setup();
    return () => {
      cancelled = true;
      stripeRef.current = null;
      cardRef.current = null;
      if (card) {
        card.unmount();
        card.destroy();
      }
    };
  }, [
    slug,
    scope,
    reloadKey,
    resolvedMode,
    elementsLocale,
    tokens.brand,
    tokens.text,
    tokens.surface,
    theme.typography.fontFamily,
  ]);

  const tokenize = useCallback(async (): Promise<TokenizeResult> => {
    const stripe = stripeRef.current;
    const card = cardRef.current;
    if (!stripe || !card) return { ok: false, message: null };
    // cardToken is a Stripe PaymentMethod id (pm_…). If tmm-api rules it is a card token (tok_…),
    // switch this call to stripe.createToken(card) → token.id.
    const result = await stripe.createPaymentMethod({
      type: 'card',
      card: card as never,
    });
    if (result.error) {
      return { ok: false, message: result.error.message ?? null };
    }
    const id = result.paymentMethod?.id;
    if (typeof id === 'string' && id.length > 0) {
      return { ok: true, token: id };
    }
    return { ok: false, message: null };
  }, []);

  const retry = useCallback(() => {
    clearStripeMemo();
    setReloadKey((n) => n + 1);
  }, []);

  return { status, complete, cardMessage, tokenize, retry };
}
